import React, { useMemo } from 'react';
import { Monitor, Calendar, FileQuestion, Star, Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModernModuleDashboard from '../../shared/components/dashboard/ModernModuleDashboard';
import { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import {
  buildDashboardCharts,
  pipelineFromKpis,
  sumRowValues,
  withKpiVariants,
} from '../../shared/components/dashboard/moduleDashboardCharts';
import { usePortalEmployeeContext } from './PortalEmployeeContext';
import { ALL_HR_DATA_REFRESH_EVENTS } from '../ressources-humaines/hrRefreshEvents';
import { EmployeePortalScopeAlert } from './employeePortalShared';

export default function EmployeePortalDashboardPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.employee_portal.pages.dashboardPage.${key}`, opts);
  const { employeeId, email, profileResolved, scopeReady } = usePortalEmployeeContext();
  const apiQueryParams = useMemo(() => {
    if (employeeId) return { employee_id: employeeId };
    if (email) return { email };
    return null;
  }, [employeeId, email]);
  const showScopeBlock = profileResolved && !scopeReady;

  return (
    <>
      {showScopeBlock ? (
        <EmployeePortalScopeAlert />
      ) : null}
    <ModernModuleDashboard
      apiEndpoint="/employee-portal/dashboard"
      apiQueryParams={apiQueryParams}
      refreshEvents={ALL_HR_DATA_REFRESH_EVENTS}
      title={p('title', { defaultValue: 'لوحة بوابة الموظف' })}
      subtitle={p('subtitle', { defaultValue: 'ملخص الإجازات والطلبات والتدريب' })}
      icon={Monitor}
      accent="from-teal-600 to-cyan-900"
      recentTitle={p('recent_announcements', { defaultValue: 'آخر الإعلانات' })}
      quickLinks={[
        { label: p('link_profile', { defaultValue: 'ملفي' }), href: '/employee-portal/profile', icon: Monitor },
        { label: p('link_leaves', { defaultValue: 'الإجازات' }), href: '/employee-portal/leaves', icon: Calendar },
        { label: p('link_requests', { defaultValue: 'طلبات الخدمة' }), href: '/employee-portal/service-requests', icon: FileQuestion },
        { label: p('link_performance', { defaultValue: 'تقييمي' }), href: '/employee-portal/performance', icon: Star },
      ]}
      mapData={(data) => {
        const safe = data && typeof data === 'object' ? data : {};
        const kpis = safe.kpis || {};
        const chartRows = safe.chartRows || [];
        const announcements = chartRows.find((r) => String(r.label).includes('إعلان'))?.value ?? 0;

        return {
          hero: {
            label: p('kpi_leave_balance', { defaultValue: 'رصيد الإجازات' }),
            value: kpis.leaveBalance ?? 0,
            icon: Calendar,
          },
          kpis: withKpiVariants(sliceQawaemStats([
            { label: p('kpi_pending_requests', { defaultValue: 'طلبات معلقة' }), value: kpis.pendingRequests ?? 0, icon: FileQuestion },
            { label: p('kpi_training_hours', { defaultValue: 'ساعات التدريب' }), value: kpis.trainingHours ?? 0, icon: Monitor },
            { label: p('kpi_performance', { defaultValue: 'تقييم الأداء' }), value: kpis.performanceScore ?? 0, icon: Star },
            {
              label: p('kpi_announcements', { defaultValue: 'الإعلانات' }),
              value: Number(announcements) || sumRowValues(chartRows),
              icon: Bell,
            },
          ], 4)),
          charts: buildDashboardCharts({
            pipeline: {
              title: p('chart_summary', { defaultValue: 'ملخص النشاط' }),
              subtitle: p('chart_pipeline_subtitle', { defaultValue: 'طلبات وتدريب وتقييم' }),
              alwaysShow: true,
              rows: pipelineFromKpis([
                { label: p('kpi_pending_requests', { defaultValue: 'طلبات معلقة' }), value: kpis.pendingRequests ?? 0 },
                { label: p('kpi_training_hours', { defaultValue: 'ساعات التدريب' }), value: kpis.trainingHours ?? 0 },
                { label: p('kpi_performance', { defaultValue: 'تقييم الأداء' }), value: kpis.performanceScore ?? 0 },
              ]),
            },
            status: {
              title: p('chart_status_title', { defaultValue: 'حالة الطلبات' }),
              subtitle: p('chart_status_subtitle', { defaultValue: 'معلقة مقابل مكتملة' }),
              active: Math.max(0, Number(announcements) || 0),
              inactive: kpis.pendingRequests ?? 0,
              alwaysShow: true,
            },
            ranked: {
              title: p('chart_ranked_title', { defaultValue: 'نشاط البوابة' }),
              subtitle: p('chart_ranked_subtitle', { defaultValue: 'طلبات وإعلانات' }),
              rows: chartRows,
            },
          }),
          recentItems: safe.recentItems || [],
        };
      }}
    />
    </>
  );
}
